import { X, Shield, ChevronDown } from "lucide-react";
import { PRIVACY_BODY } from "@/lib/privacy";
import { renderMarkdownBody } from "@/lib/simpleMarkdown";
import { useScrollCue } from "@/hooks/useScrollCue";

/**
 * iter57 — Privacy Notice modal.
 *
 * Informational only — no acceptance gate, no version tracking. Mirrors
 * the markdown-ish render rules of `TermsModal.jsx` so the two notices
 * feel visually consistent.
 *
 * Linked from:
 *   1. Landing page (next to the Terms link in the Request League form).
 *   2. AdminPanel footer (link under settings).
 *
 * If we ever need versioning (e.g. to re-prompt admins on material
 * changes), we can lift the gating pattern from `TermsModal.jsx`.
 *
 * iter87.199.145 — Swapped the bespoke line-by-line renderer (which
 * dropped hard-wrapped continuation lines into their own paragraph,
 * fragmenting bullets across the modal) for the shared
 * `renderMarkdownBody` used by `/terms`, `/privacy`, and
 * `/terms/pro-plus-billing`.
 * iter87.199.146 — Added a "more below" scroll cue.
 */
export default function PrivacyModal({ open, onClose }) {
    const [bodyRef, showScrollCue] = useScrollCue([open]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            data-testid="privacy-modal-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="brutal-card max-w-2xl w-full max-h-[85dvh] flex flex-col"
                data-testid="privacy-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between gap-3 p-5 border-b-2 border-[#222]">
                    <div className="flex items-center gap-2 min-w-0">
                        <Shield className="w-5 h-5 text-cyber shrink-0" />
                        <h2 className="font-display font-black text-xl uppercase truncate">Privacy Notice</h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="brutal-btn-ghost p-1.5"
                        data-testid="privacy-modal-close-btn"
                        aria-label="Close privacy notice"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div
                    ref={bodyRef}
                    className="overflow-y-auto px-5 py-3 flex-1 space-y-2"
                    data-testid="privacy-modal-body"
                >
                    {renderMarkdownBody(PRIVACY_BODY)}
                    {showScrollCue && (
                        <div
                            className="sticky bottom-0 -mx-5 -mb-3 h-10 bg-gradient-to-t from-[#121212] to-transparent flex items-end justify-center pb-1 pointer-events-none"
                            data-testid="privacy-modal-scroll-cue"
                        >
                            <ChevronDown className="w-4 h-4 text-white/50 animate-bounce" />
                        </div>
                    )}
                </div>
                <div className="p-3 border-t-2 border-[#222] bg-ink flex items-center justify-between">
                    <p className="text-[10px] text-zinc-600 font-mono">Informational — no acceptance required.</p>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="privacy-modal-close-footer-btn"
                        className="brutal-btn-ghost text-xs"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
